import { Loader2 } from "lucide-react";
import { progressSteps } from "../../lib/bookingFlow";
import { Icon } from "../msd/Icon";
import { Btn } from "../msd/ui";

const ROW = {
  done: { ring: "bg-[#17B26A] text-white", text: "text-msd-ink" },
  active: { ring: "bg-msd-btn text-white", text: "font-semibold text-msd-ink" },
  error: { ring: "bg-[#F04438] text-white", text: "font-semibold text-msd-brick-ink" },
  pending: { ring: "bg-msd-ground text-msd-faint", text: "text-msd-muted" },
};

// Shown in place of the form's last step once "Create group" is pressed. All the
// waiting happens here: it lists what is being done, which step it is on, and
// what to do if it stalls or fails.
export default function BookingProgress({ flow, hasUploads = false }) {
  const { state, retry, keepWaiting, goToDesk } = flow;
  const steps = progressSteps(state.phase, { hasUploads, failed: state.failed });

  return (
    <div className="flex flex-col gap-[18px]" data-testid="booking-progress" role="status" aria-live="polite">
      <div>
        <h3 className="m-0 text-[19px] font-bold tracking-[-0.015em] text-msd-ink">{state.failed ? "That didn't work" : "Setting up the group"}</h3>
        <p className="m-0 mt-1 text-[14px] text-msd-muted">
          {state.failed
            ? "Nothing is lost. You can try again."
            : "This takes about a minute. Keep this page open; we'll take you to the desk when the customer has been invited."}
        </p>
      </div>

      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {steps.map((s) => {
          const t = ROW[s.state];
          return (
            <li key={s.key} className="flex items-center gap-3" data-testid={`progress-${s.key}`} data-state={s.state}>
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${t.ring}`}>
                {s.state === "done" ? (
                  <Icon name="check" size={14} stroke={2.6} />
                ) : s.state === "active" ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : s.state === "error" ? (
                  <Icon name="x" size={14} stroke={2.6} />
                ) : null}
              </span>
              <span className={`text-[15px] ${t.text}`}>{s.label}</span>
            </li>
          );
        })}
      </ol>

      {state.failed && (
        <div className="flex flex-col gap-3 rounded-[14px] bg-msd-brick-soft px-4 py-[14px]" data-testid="booking-progress-error">
          <p className="m-0 flex items-start gap-2 text-[14px] text-msd-brick-ink">
            <Icon name="warn" size={18} stroke={2} color="#F04438" className="mt-px shrink-0" />
            <span className="min-w-0 break-words">{state.error}</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <Btn variant="red" size="sm" onClick={retry} data-testid="booking-retry">
              Try again
            </Btn>
            {state.rowId && (
              <Btn size="sm" onClick={goToDesk}>
                Open the desk anyway
              </Btn>
            )}
          </div>
        </div>
      )}

      {state.slow && !state.failed && (
        <div className="flex flex-col gap-3 rounded-[14px] bg-msd-amber-soft px-4 py-[14px]" data-testid="booking-progress-slow">
          <p className="m-0 text-[14px] text-msd-amber-ink">This is taking longer than usual. The booking is saved, and it will keep going in the background.</p>
          <div className="flex flex-wrap gap-2">
            <Btn variant="pri" size="sm" onClick={keepWaiting}>
              Keep waiting
            </Btn>
            <Btn size="sm" onClick={goToDesk}>
              Open the desk
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}
